"use client"

import type React from "react"

interface TermsOfServiceProps {
  onBack?: () => void;
}

const TermsOfService: React.FC<TermsOfServiceProps> = ({ onBack }) => {

  return (
    <div className="min-h-screen py-12">
      <div className="max-w-4xl mx-auto px-6">
        {/* Header */}
        <div className="mb-8">
          
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Terms of Service</h1>
            <p className="text-gray-600">Last updated: August 16, 2025</p>
          </div>
        </div>

        {/* Content */}
        <div className="bg-white rounded-3xl shadow-xl border border-gray-200 p-8 md:p-12">
          <div className="prose prose-lg max-w-none">
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Acceptance of Terms</h2>
              <p className="text-gray-700 leading-relaxed">
                By accessing and using the services provided by TopClip, you agree to be bound by these Terms of Service ("TOS"). If you do not agree to these terms, please do not use our services.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Description of Service</h2>
              <p className="text-gray-700 leading-relaxed">
                TopClip provides AI-powered editing tools designed to assist users in creating and editing content. Our services are subject to the terms and conditions outlined in this TOS.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Subscriptions, Billing and Automatic Renewal</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                TopClip paid subscription plans are recurring subscriptions. Depending on the plan you select, your subscription automatically renews every month, every three months (quarterly), or every year (annually), unless you cancel before your next renewal date.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                By purchasing a subscription, you acknowledge and agree that it will renew automatically until canceled, and authorize TopClip, through its payment processor, to charge your selected payment method for the amount and billing interval disclosed at checkout, including applicable taxes, at purchase and on each renewal. Quarterly and annual plans are charged for the full three-month or twelve-month period. Any monthly equivalent displayed is for comparison only.
              </p>
              <p className="text-gray-700 leading-relaxed">
                If we change the price or billing terms of your existing subscription, we will notify you before the change takes effect and obtain your consent if required by applicable law. If you do not wish to continue under the updated terms, you may cancel before they take effect.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Use of Service</h2>
              <ul className="list-disc list-inside text-gray-700 space-y-2 leading-relaxed">
                <li>You agree to use TopClip's services only for lawful purposes and in a manner that does not infringe the rights of, restrict, or inhibit anyone else's use and enjoyment of the service.</li>
                <li>TopClip grants you a non-exclusive, non-transferable, limited right to access and use the services for your personal, non-commercial purposes, subject to these TOS.</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">User Conduct</h2>
              <ul className="list-disc list-inside text-gray-700 space-y-2 leading-relaxed">
                <li>You are solely responsible for any content you create, transmit, or display while using TopClip's services and for the consequences of your actions.</li>
                <li>You agree not to upload, communicate, transmit, or otherwise make available any content that is unlawful, harmful, threatening, abusive, harassing, tortious, defamatory, vulgar, obscene, libelous, invasive of another's privacy, hateful, or racially, ethnically, or otherwise objectionable.</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Intellectual Property</h2>
              <ul className="list-disc list-inside text-gray-700 space-y-2 leading-relaxed">
                <li>You retain all ownership rights to the content you create or upload. However, by using TopClip's services, you grant TopClip a worldwide, non-exclusive, royalty-free license to use, reproduce, distribute, and display the content in connection with the service.</li>
                <li>TopClip owns all rights to the software, applications, and intellectual property related to the service.</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Termination</h2>
              <p className="text-gray-700 leading-relaxed">
                TopClip reserves the right to terminate your access to the services without notice for any breach of these TOS.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Refund Policy</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                TopClip does not offer refunds in accordance to the full refund policy described below.
              </p>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3">No Refunds</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                ALL SALES ARE FINAL. The Company does not offer refunds for any products or services under any circumstances once a transaction has been completed. By making a purchase and getting immediate access to our service, you explicitly acknowledge and agree that you have read, understood, and accepted this strict no-refund policy, and expressly consent and acknowledge that you lose your statutory right of withdrawal once access begins. No exceptions will be made to this no-refund policy, including but not limited to claims of dissatisfaction, accidental purchases, or changes in personal circumstances.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">Cancellation Policy</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-2 leading-relaxed mb-4">
                <li><strong>Eligibility:</strong> All subscribers may cancel at any time at their choosing, no approval required. You may do so by following the steps outlined below.</li>
                <li><strong>Cancellation Process:</strong> To cancel your subscription, open Account, select Manage Billing, then choose Cancel subscription in Stripe. If you wish to avoid the next subscription charge, you must cancel before your next renewal date.</li>
                <li><strong>Service Continuation:</strong> Upon cancellation, the plan will remain active until the end of the current billing cycle. The subscriber will continue to have full access to all features during this period.</li>
                <li><strong>No Further Renewals:</strong> Once cancellation is confirmed, your subscription will not renew for another billing period.</li>
                <li><strong>Outstanding Payments:</strong> Cancellation does not cancel amounts already due for a billing period that began before you canceled. If a payment for that period fails, TopClip, through its payment processor, may retry and collect that payment after you cancel because it was already due before cancellation. A successful retry pays the existing invoice; it does not start a new billing period or reactivate automatic renewal.</li>
                <li><strong>Data Retention:</strong> Generally, data will be kept in case of reactivation. However, the Company reserves the right to delete any data upon cancellation. Please contact support if you wish to have your data removed in accordance to applicable privacy laws and our policy provisions.</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Age Restriction</h2>
              <p className="text-gray-700 leading-relaxed">
                TopClip's services are not intended for use by individuals under the age of 18. By using our services, you represent and warrant that you are at least 18 years of age. If we become aware that a user is under 18, we reserve the right to terminate their account and access to our services immediately.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Disclaimer of Warranties</h2>
              <p className="text-gray-700 leading-relaxed">
                The services are provided "as is" and "as available" without warranty of any kind, either express or implied. TopClip does not guarantee that the service will meet your requirements or be available on an uninterrupted, secure, or error-free basis.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Disclaimers</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                This website and the products offered are not affiliated with, sponsored by, or endorsed by any social media platform, including YouTube, Instagram, Facebook, or TikTok.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                Any examples of growth, reach, income, or earnings shown on this site are for illustrative purposes only and do not represent typical or guaranteed results. Actual outcomes will vary depending on factors such as your content, effort, consistency, commitment, and audience.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                Earnings and income representations made by TopClip are aspirational statements only, and TopClip does not promise or imply that you will achieve similar results. This is not a "get rich quick" program.
              </p>
              <p className="text-gray-700 leading-relaxed">
                By using this site, you agree to our Terms of Service and Privacy Policy.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Limitation of Liability</h2>
              <p className="text-gray-700 leading-relaxed">
                TopClip shall not be liable for any direct, indirect, incidental, special, consequential, or exemplary damages resulting from your use of the services.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Changes to Terms</h2>
              <p className="text-gray-700 leading-relaxed">
                TopClip reserves the right to modify these TOS at any time. Changes to subscription prices or billing terms are subject to the notice and consent requirements described above. Your continued use of the service after such changes constitutes your acceptance of the new TOS.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Governing Law</h2>
              <p className="text-gray-700 leading-relaxed">
                This Policy shall be governed by and construed in accordance with the laws of the jurisdiction applicable to TopClip, without regard to its conflict of law provisions.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Contact Information</h2>
              <p className="text-gray-700 leading-relaxed">
                For any questions regarding these TOS, please contact us at{" "}
                <a href="mailto:hello@topclip.ai" className="text-blue-600 hover:text-blue-800 underline">
                  hello@topclip.ai
                </a>
                .
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  )
}

export default TermsOfService
